import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Eye, Play, Download, Trash2 } from 'lucide-react'
import { documentsApi, type DocumentFile } from '../api/documents'
import { waApi, type WaLeadMedia } from '../api/whatsapp'
import { useAuthStore } from '../store/auth'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { DocumentViewerModal } from './DocumentViewerModal'
import { MediaViewerModal, type WaViewerItem } from '../pages/whatsapp/MediaViewerModal'
import { WaVoicePlayer } from './WaVoicePlayer'
import { formatDate } from '../lib/utils'
import toast from 'react-hot-toast'

interface Props {
  companyId: string
  leadId: string
}

type FilterType = 'all' | 'image' | 'doc' | 'video'

// Unified row: either an uploaded document or a WhatsApp media message (listed, not re-stored).
type Item =
  | { kind: 'doc'; id: string; name: string; mimeType: string; size: number; date: string; by: string; doc: DocumentFile }
  | { kind: 'wa'; id: string; name: string; mimeType: string; size: number; date: string; by: string; wa: WaLeadMedia }

function formatBytes(bytes: number): string {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function fileIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '🖼️'
  if (mimeType === 'application/pdf') return '📄'
  if (mimeType.startsWith('video/')) return '🎬'
  if (mimeType.startsWith('audio/')) return '🎤'
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝'
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊'
  return '📎'
}

function fileTypeTag(mimeType: string): { label: string; className: string } {
  if (mimeType.startsWith('image/')) return { label: 'Image', className: 'bg-pink-100 text-pink-700' }
  if (mimeType === 'application/pdf') return { label: 'PDF', className: 'bg-red-100 text-red-700' }
  if (mimeType.startsWith('video/')) return { label: 'Video', className: 'bg-purple-100 text-purple-700' }
  if (mimeType.startsWith('audio/')) return { label: 'Audio', className: 'bg-orange-100 text-orange-700' }
  if (mimeType.includes('word') || mimeType.includes('document')) return { label: 'Word', className: 'bg-blue-100 text-blue-700' }
  if (mimeType.includes('sheet') || mimeType.includes('excel')) return { label: 'Excel', className: 'bg-green-100 text-green-700' }
  return { label: 'File', className: 'bg-gray-100 text-gray-600' }
}

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50 MB — matches server limit

const ALLOWED_EXTENSIONS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg',
  'pdf',
  'mp4', 'webm', 'ogg',
  'doc', 'docx', 'xls', 'xlsx',
  'txt', 'csv',
  'zip',
])

function fileExt(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : ''
}

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) return `"${file.name}" exceeds the 50 MB limit`
  const ext = fileExt(file.name)
  if (!ALLOWED_EXTENSIONS.has(ext)) return `"${file.name}" — file type not allowed`
  return null
}

const INLINE_VIEWABLE = new Set(['image/png','image/jpeg','image/jpg','image/gif','image/webp','image/svg+xml','video/mp4','video/webm','video/ogg','application/pdf'])

function isViewable(mimeType: string): boolean {
  return INLINE_VIEWABLE.has(mimeType.toLowerCase())
}

function filterMatch(mimeType: string, filter: FilterType): boolean {
  if (filter === 'all') return true
  if (filter === 'image') return mimeType.startsWith('image/')
  if (filter === 'video') return mimeType.startsWith('video/')
  if (filter === 'doc') return !mimeType.startsWith('image/') && !mimeType.startsWith('video/')
  return true
}

export function DocumentsTab({ companyId, leadId }: Props) {
  const { user } = useAuthStore()
  const qc = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [filter, setFilter] = useState<FilterType>('all')
  const [dragging, setDragging] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<DocumentFile | null>(null)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerIndex, setViewerIndex] = useState(0)
  const [waViewerIndex, setWaViewerIndex] = useState<number | null>(null)
  const [uploading, setUploading] = useState(false)

  const canDelete = user?.role === 'company_owner' || user?.role === 'administrator'

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ['documents', companyId, leadId],
    queryFn: () => documentsApi.list(companyId, leadId).then(r => r.data),
    enabled: !!companyId && !!leadId,
  })
  const { data: waMedia = [] } = useQuery({
    queryKey: ['wa-lead-media', companyId, leadId],
    queryFn: () => waApi.leadMedia(companyId, leadId),
    enabled: !!companyId && !!leadId,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => documentsApi.softDelete(companyId, leadId, id),
    onSuccess: () => {
      toast.success('Document deleted')
      qc.invalidateQueries({ queryKey: ['documents', companyId, leadId] })
    },
    onError: () => toast.error('Failed to delete document'),
  })

  const uploadFile = async (file: File) => {
    const err = validateFile(file)
    if (err) { toast.error(err); return }
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      await documentsApi.upload(companyId, leadId, formData)
      qc.invalidateQueries({ queryKey: ['documents', companyId, leadId] })
      toast.success(`${file.name} uploaded`)
    } catch (err: any) {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    Array.from(e.dataTransfer.files).forEach(uploadFile)
  }

  const handleDownloadDoc = async (file: DocumentFile) => {
    try {
      const res = await documentsApi.stream(companyId, leadId, file.id)
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = file.originalName
      a.click()
      URL.revokeObjectURL(url)
    } catch { toast.error('Download failed') }
  }

  const handleDownloadWa = async (wa: WaLeadMedia) => {
    try {
      const url = await waApi.mediaBlob(companyId, wa.messageId)
      const a = document.createElement('a')
      a.href = url
      a.download = wa.fileName ?? 'whatsapp-file'
      a.click()
    } catch { toast.error('Download failed') }
  }

  const openWaViewer = (wa: WaLeadMedia) => {
    const at = waViewerItems.findIndex(x => x.messageId === wa.messageId)
    if (at >= 0) setWaViewerIndex(at)
  }

  // Merge both sources into one date-sorted list.
  const items: Item[] = [
    ...docs.map((d): Item => ({
      kind: 'doc', id: `doc-${d.id}`, name: d.originalName, mimeType: d.mimeType, size: d.sizeBytes,
      date: d.createdAt,
      by: d.uploadedByUser ? `${d.uploadedByUser.firstName} ${d.uploadedByUser.lastName}` : 'Unknown',
      doc: d,
    })),
    ...waMedia.map((w): Item => ({
      kind: 'wa', id: `wa-${w.messageId}`, name: w.fileName ?? 'WhatsApp file', mimeType: w.mimeType ?? '', size: w.size ?? 0,
      date: typeof w.waTimestamp === 'string' ? w.waTimestamp : new Date(w.waTimestamp).toISOString(),
      by: w.direction === 'out' ? (w.sender ?? 'You') : (w.sender ?? 'Contact'),
      wa: w,
    })),
  ].sort((a, b) => +new Date(b.date) - +new Date(a.date))

  const filtered = items.filter(it => filterMatch(it.mimeType, filter))
  // WhatsApp attachments the viewer can navigate, in the order shown so the
  // arrows follow the list the user is looking at (including the active filter).
  const waViewerItems: WaViewerItem[] = filtered
    .filter(it => it.kind === 'wa' && isViewable(it.mimeType))
    .map(it => ({ messageId: (it as Extract<Item, { kind: 'wa' }>).wa.messageId, mime: it.mimeType, name: it.name }))
  // Doc viewer navigates only among uploaded, viewable docs.
  const viewableDocs = docs.filter(f => isViewable(f.mimeType))

  const openDocViewer = (file: DocumentFile) => {
    setViewerIndex(viewableDocs.findIndex(f => f.id === file.id))
    setViewerOpen(true)
  }

  const counts = {
    all: items.length,
    image: items.filter(it => it.mimeType.startsWith('image/')).length,
    video: items.filter(it => it.mimeType.startsWith('video/')).length,
    doc: items.filter(it => !it.mimeType.startsWith('image/') && !it.mimeType.startsWith('video/')).length,
  }

  return (
    <div className="p-4">
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer mb-3 transition-colors ${
          dragging ? 'border-primary-400 bg-primary-50' : 'border-gray-200 bg-white hover:border-primary-300'
        }`}
      >
        <input ref={fileInputRef} type="file" multiple className="hidden"
          onChange={e => Array.from(e.target.files ?? []).forEach(uploadFile)} />
        <p className="text-xs text-gray-500">
          {uploading ? 'Uploading…' : <><span className="text-primary-600 font-medium">Browse</span> or drag &amp; drop</>}
        </p>
        <p className="text-[11px] text-gray-400 mt-0.5">Images · PDFs · Videos · Word · Excel · CSV · ZIP · up to 50 MB</p>
      </div>

      {items.length > 0 && (
        <div className="flex gap-1.5 mb-3 flex-wrap">
          {([['all', `All (${counts.all})`], ['image', `🖼 Images (${counts.image})`], ['doc', `📄 Docs (${counts.doc})`], ['video', `🎬 Videos (${counts.video})`]] as [FilterType, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                filter === key ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {isLoading && <p className="text-xs text-gray-400 text-center py-4">Loading…</p>}
      {!isLoading && items.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-6">No documents yet</p>
      )}
      {!isLoading && filtered.length > 0 && (
        <div className="divide-y divide-gray-50">
          {filtered.map(it => {
            const tag = fileTypeTag(it.mimeType)
            const viewable = isViewable(it.mimeType)
            const isVideo = it.mimeType.startsWith('video/')
            // Voice notes and other audio play in place; they are not in
            // INLINE_VIEWABLE because the modal viewer is the wrong shape for them.
            const isAudio = it.mimeType.startsWith('audio/')
            return (
              <div key={it.id} className="flex items-center gap-2.5 py-2.5">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-base flex-shrink-0">
                  {fileIcon(it.mimeType)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{it.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${tag.className}`}>{tag.label}</span>
                    {it.kind === 'wa' && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700">WhatsApp</span>
                    )}
                    <span className="text-[11px] text-gray-400">
                      {it.by} · {formatBytes(it.size)} · {formatDate(it.date)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {isAudio && (
                    <WaVoicePlayer
                      title="Play recording"
                      load={() => it.kind === 'wa'
                        ? waApi.mediaBlob(companyId, it.wa.messageId)
                        : documentsApi.stream(companyId, leadId, it.doc.id).then(r => URL.createObjectURL(r.data))}
                    />
                  )}
                  {viewable && (
                    <button
                      onClick={() => it.kind === 'doc' ? openDocViewer(it.doc) : openWaViewer(it.wa)}
                      className="btn btn-ghost btn-sm p-1.5 text-gray-400 hover:text-indigo-600"
                      title={isVideo ? 'Play video' : 'View'}
                    >
                      {isVideo ? <Play className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  <button
                    onClick={() => it.kind === 'doc' ? handleDownloadDoc(it.doc) : handleDownloadWa(it.wa)}
                    className="btn btn-ghost btn-sm p-1.5 text-gray-400 hover:text-gray-700"
                    title="Download"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  {it.kind === 'doc' && canDelete && (
                    <button
                      onClick={() => setDeleteTarget(it.doc)}
                      className="btn btn-ghost btn-sm p-1.5 text-gray-300 hover:text-red-500"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {viewerOpen && viewableDocs.length > 0 && (
        <DocumentViewerModal
          files={viewableDocs}
          initialIndex={viewerIndex}
          companyId={companyId}
          leadId={leadId}
          onClose={() => setViewerOpen(false)}
        />
      )}

      {waViewerIndex !== null && waViewerItems.length > 0 && (
        <MediaViewerModal
          companyId={companyId}
          items={waViewerItems}
          index={waViewerIndex}
          onIndexChange={setWaViewerIndex}
          onClose={() => setWaViewerIndex(null)}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => { if (deleteTarget) deleteMutation.mutate(deleteTarget.id) }}
        title="Delete document?"
        message={`"${deleteTarget?.originalName}" will be moved to the recycle bin.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}
